import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import API_BASE_URL from '../config/apiBaseUrl';
import { getAuthHeaders } from '../services/authFetch';
import './PriceAdjustmentCalculator.css';

const TIER_COLORS = {
  TOP: { bg: '#c8e6c9', color: '#1b5e20' },
  Rare: { bg: '#dcedc8', color: '#33691e' },
  Commun: { bg: '#fff9c4', color: '#827717' },
  'Tres Commun': { bg: '#ffe0b2', color: '#e65100' },
  Trash: { bg: '#ffcdd2', color: '#b71c1c' },
};

const TierChip = ({ tier }) => {
  const style = TIER_COLORS[tier] || { bg: '#eee', color: '#333' };
  return (
    <Chip
      label={tier}
      size="small"
      style={{ backgroundColor: style.bg, color: style.color, fontWeight: 'bold' }}
    />
  );
};

const RULE_LABELS = {
  part1:  { label: 'P1 – <30j / s360<30 (+20%)',         bg: '#c8e6c9', color: '#1b5e20' },
  part2:  { label: 'P2 – <30j / s360 30-100 (+10%)',     bg: '#dcedc8', color: '#33691e' },
  part3:  { label: 'P3 – <30j / s360 101-200 (+5%)',     bg: '#fff9c4', color: '#827717' },
  part4:  { label: 'P4 – <30j / s360>200 (0%)',          bg: '#ffe0b2', color: '#e65100' },
  part5:  { label: 'P5 – 30-60j / s360<30 (+10%)',       bg: '#b3e5fc', color: '#01579b' },
  part6:  { label: 'P6 – 30-60j / s360 30-100 (+5%)',    bg: '#b2ebf2', color: '#006064' },
  part7:  { label: 'P7 – 30-60j / s360 101-200 (0%)',    bg: '#e8f5e9', color: '#2e7d32' },
  part8:  { label: 'P8 – 30-60j / s360>200 (-5%)',       bg: '#fce4ec', color: '#880e4f' },
  part9:  { label: 'P9 – 61-200j / s360<30 (+5%)',       bg: '#e1bee7', color: '#4a148c' },
  part10: { label: 'P10 – 61-200j / s360 30-100 (0%)',   bg: '#d1c4e9', color: '#311b92' },
  part11: { label: 'P11 – 61-200j / s360 101-200 (-5%)', bg: '#ffe082', color: '#ff6f00' },
  part12: { label: 'P12 – 61-200j / s360>200 (-15%)',    bg: '#ffcdd2', color: '#b71c1c' },
  part13: { label: 'P13 – >200j / s360<30 (0%)',         bg: '#cfd8dc', color: '#263238' },
  part14: { label: 'P14 – >200j / s360 30-100 (-10%)',   bg: '#b0bec5', color: '#263238' },
  part15: { label: 'P15 – >200j / s360 101-200 (-15%)',  bg: '#ef9a9a', color: '#b71c1c' },
  part16: { label: 'P16 – >200j / s360>200 (-20%)',      bg: '#e53935', color: '#fff' },
  legacy: { label: 'Rareté (sans couverture)',           bg: '#eceff1', color: '#37474f' },
};

const formatCoefficient = (coefficient) => {
  const value = Number(coefficient) || 0;
  const percent = Math.round(value * 100);
  return `${percent > 0 ? '+' : ''}${percent}%`;
};

const RuleChip = ({ item }) => {
  const rule = item?.pricingRule;
  const base = RULE_LABELS[rule] || { bg: '#eee', color: '#333', label: rule };

  let label = base.label;
  if (rule === 'legacy') {
    const jrStock = item?.jrStock ?? 'N/A';
    const stock360 = item?.stock360 ?? 'N/A';
    const coeff = formatCoefficient(item?.rarityCoefficient);
    label = `Fallback – Jr.Stock ${jrStock}j / S360 ${stock360} ${coeff}`;
  }

  return (
    <Chip
      label={label}
      size="small"
      style={{ backgroundColor: base.bg, color: base.color, fontWeight: 'bold' }}
    />
  );
};

const toNumberOrNull = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const normalized = typeof value === 'string' ? value.replace(',', '.') : value;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const getCalculatedPriceFromRow = (item) => {
  const directCandidates = [
    item.calculatedPrice,
    item.calculated_price,
    item.algoPrice,
    item.recommendedPrice,
    item.recommended_price,
  ];

  for (const candidate of directCandidates) {
    const parsed = toNumberOrNull(candidate);
    if (parsed !== null) {
      return parsed;
    }
  }

  const ourPrice = toNumberOrNull(item.ourPrice);
  const coefficient = toNumberOrNull(item.rarityCoefficient);

  if (ourPrice !== null && coefficient !== null) {
    return Number((ourPrice * (1 + coefficient)).toFixed(2));
  }

  return null;
};

const getCoverageClass = (coverageDays) => {
  if (coverageDays == null) {
    return '';
  }

  if (coverageDays < 30) {
    return 'coverage-low';
  }

  if (coverageDays > 365) {
    return 'coverage-high';
  }

  return 'coverage-mid';
};

export default function PriceAdjustmentCalculator() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportingDetailed, setExportingDetailed] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await fetch(
          `${API_BASE_URL}/pricing/adjustment?page=${page + 1}&limit=${rowsPerPage}`,
          {
            headers: getAuthHeaders(),
          }
        );

        if (!response.ok) {
          throw new Error(`Erreur API: ${response.statusText}`);
        }

        const result = await response.json();
        setRows(result.data || []);
        setTotal(result.total || 0);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [page, rowsPerPage]);

  const handleExport = async () => {
    await handleDownloadExport(
      `${API_BASE_URL}/pricing/adjustment/export`,
      `ajustement_prix_${new Date().toISOString().slice(0, 10)}.xlsx`,
      setExporting
    );
  };

  const handleDetailedExport = async () => {
    await handleDownloadExport(
      `${API_BASE_URL}/pricing/adjustment/export/detailed`,
      `ajustement_prix_detaille_${new Date().toISOString().slice(0, 10)}.xlsx`,
      setExportingDetailed
    );
  };

  const handleDownloadExport = async (url, filename, setLoadingState) => {
    setLoadingState(true);
    setError(null);

    try {
      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
        headers: {
          ...getAuthHeaders(),
          'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        },
      });

      if (!response.ok) {
        throw new Error(`Erreur lors de l'export: ${response.statusText}`);
      }

      const contentType = (response.headers.get('Content-Type') || '').toLowerCase();
      const isExcelResponse =
        contentType.includes('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') ||
        contentType.includes('application/octet-stream');

      if (!isExcelResponse) {
        const errorPayload = await response.text();
        throw new Error(errorPayload || 'Le serveur n\'a pas renvoye un fichier Excel valide.');
      }

      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const contentDisposition = response.headers.get('Content-Disposition') || '';
      const match = contentDisposition.match(/filename\*?=(?:UTF-8''|"?)([^";]+)/i);
      const serverFilename = match?.[1] ? decodeURIComponent(match[1].replace(/"/g, '')) : null;
      link.href = objectUrl;
      link.download = serverFilename || filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingState(false);
    }
  };

  const handlePageChange = (_event, nextPage) => {
    setPage(nextPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading) {
    return (
      <Box className="price-adjustment-loading">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="price-adjustment-page">
      <Box className="price-adjustment-toolbar">
        <Typography variant="h6" className="price-adjustment-title">
          Ajustement de prix ({total} references)
        </Typography>
        <Box className="price-adjustment-actions">
          <Button
            variant="contained"
            color="primary"
            onClick={handleExport}
            disabled={exporting || exportingDetailed || total === 0}
            className="price-adjustment-btn"
          >
            {exporting ? 'Export...' : 'Exporter (refOEM + prix calculé)'}
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" className="price-adjustment-error">{error}</Alert>}

      {total === 0 ? (
        <Alert severity="info">
          Aucune reference en stock. Importez d'abord un stock (onglet Importer).
        </Alert>
      ) : (
        <TableContainer component={Paper} className="price-adjustment-table-wrapper">
          <Table size="small" stickyHeader className="price-adjustment-table">
            <TableHead>
              <TableRow className="price-adjustment-header-row">
                <TableCell className="col-oem">Reference OEM</TableCell>
                <TableCell align="right">Notre prix cumule</TableCell>
                <TableCell align="right">Mediane marche</TableCell>
                <TableCell align="right">Moyenne Opisto</TableCell>
                <TableCell align="right">Stock360</TableCell>
                <TableCell align="right">Quantite</TableCell>
                <TableCell align="right">Qt Vendu/an</TableCell>
                <TableCell align="right">Couverture (j)</TableCell>
                <TableCell align="center">Rarete</TableCell>
                <TableCell align="center">Règle</TableCell>
                <TableCell align="right">Prix calcule</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((item, idx) => (
                <TableRow key={`${item.oemReference}-${idx}`}>
                  <TableCell className="col-oem">{item.oemReference}</TableCell>
                  <TableCell align="right">{item.ourPrice?.toFixed(2)} EUR</TableCell>
                  <TableCell align="right">
                    {(item.medianSummary > 0 ? item.medianSummary : item.medianMarket)?.toFixed(2)} EUR
                  </TableCell>
                  <TableCell align="right">
                    {item.moyenneOpisto != null ? `${item.moyenneOpisto.toFixed(2)} EUR` : '-'}
                  </TableCell>
                  <TableCell align="right">{item.stock360 != null ? item.stock360 : '-'}</TableCell>
                  <TableCell align="right">{item.quantity != null ? item.quantity : '-'}</TableCell>
                  <TableCell align="right">{item.qtVendu != null ? item.qtVendu : '-'}</TableCell>
                  <TableCell align="right">
                    {(() => {
                      if (item.coverageDays != null) {
                        return (
                          <strong className={`coverage-value ${getCoverageClass(item.coverageDays)}`}>
                            {item.coverageDays}j
                          </strong>
                        );
                      } else if (item.jrStock != null) {
                        return (
                          <span title="Fallback utilisant jr_stock">
                            <strong className={`coverage-value ${getCoverageClass(item.jrStock)}`}>
                              {item.jrStock}j (jrStock)
                            </strong>
                          </span>
                        );
                      } else {
                        return '-';
                      }
                    })()}
                  </TableCell>
                  <TableCell align="center"><TierChip tier={item.tier} /></TableCell>
                  <TableCell align="center"><RuleChip item={item} /></TableCell>
                  <TableCell align="right" className="calculated-price-cell">
                    {(() => {
                      const calculatedPrice = getCalculatedPriceFromRow(item);
                      return calculatedPrice !== null ? `${calculatedPrice.toFixed(2)} EUR` : '-';
                    })()}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={handlePageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[25, 50, 100, 250]}
            labelRowsPerPage="Lignes par page"
            className="price-adjustment-pagination"
          />
        </TableContainer>
      )}
    </Box>
  );
}
